import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import type { AuctionsOfAuctionTileParams } from 'src/apis/transport-auction/types';
import { useAuctionTile } from 'src/apis/transport-auction/use-auction-tile';
import { useAuctionsOfAuctionTile } from 'src/apis/transport-auction/use-auctions-of-auction-tile';
import {
  useTransportAuctionServiceRoot,
  type OptionalTransportAuctionServiceRootRequest,
} from 'src/apis/transport-auction/use-transport-auction-service-root';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useAuctionsPageParams } from 'src/pages/transport/auctions/tiles/use-page-params';

const Page = lazy(() => import('./page'));
const ErrorPage = lazy(() => import('./error'));

export const RunningAuctionsOverviewTAS: FC = () => {
  const transportAuctionServiceRoot = useTransportAuctionServiceRoot();

  if (!transportAuctionServiceRoot.response) return <PageSpinner />;

  return <LoadData transportAuctionServiceRoot={transportAuctionServiceRoot} />;
};

const LoadData: FC<{
  transportAuctionServiceRoot: Successful<OptionalTransportAuctionServiceRootRequest>;
}> = ({ transportAuctionServiceRoot }) => {
  const { tileId } = useParams<{ tileId: string }>();
  const pageParams = useAuctionsPageParams();

  const tile = useAuctionTile({
    root: transportAuctionServiceRoot,
    tileId,
  });

  const auctionsParams = useMemo(
    () =>
      ({
        limit: pageParams.value.pageSize,
        offset: pageParams.value.start,
        sortBy: pageParams.value.sortBy,
        sortDirection: pageParams.value.sortDirection,
      }) satisfies AuctionsOfAuctionTileParams,
    [pageParams.value]
  );
  const auctions = useAuctionsOfAuctionTile({
    root: transportAuctionServiceRoot,
    tileId,
    params: auctionsParams,
  });

  const error = tile.error || auctions.error;
  if (error) return <ErrorPage error={error} />;

  if (!tile.response || !auctions.response) return <PageSpinner />;

  return <Page tile={tile} auctions={auctions} pageParams={pageParams} />;
};
